import { useSearchParams } from 'react-router-dom'
import { useMemo } from 'react'
import { SortingState } from '@tanstack/react-table'

export const SORT_PARAM_KEY = 'sort'

export function useTableSort() {
  const [searchParams, setSearchParams] = useSearchParams()

  const sorting = useMemo(() => parseSortParams(searchParams.getAll(SORT_PARAM_KEY)), [searchParams])

  const updateSorting = (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function'
      ? updaterOrValue(sorting)
      : updaterOrValue

    setSearchParams(prev => {
      prev.delete(SORT_PARAM_KEY)

      newSorting.forEach(sort => {
        prev.append(SORT_PARAM_KEY, `${sort.desc ? '-' : '+'}${sort.id}`)
      })

      return prev
    })
  }

  const toggleSort = (columnId: string) => {
    updateSorting(currentSorting => {
      const existingSort = currentSorting.find(sort => sort.id === columnId)

      if (!existingSort) {
        return [{ id: columnId, desc: false }]
      }

      if (!existingSort.desc) {
        return [{ id: columnId, desc: true }]
      }

      return []
    })
  }

  const clearAllSorts = () => {
    setSearchParams(prev => {
      prev.delete(SORT_PARAM_KEY)
      return prev
    })
  }

  const getSortDirection = (columnId: string): 'asc' | 'desc' | undefined => {
    const sortState = sorting.find(sort => sort.id === columnId)

    if (!sortState) {
      return undefined
    }

    return sortState.desc ? 'desc' : 'asc'
  }

  const getSorting = () => sorting.map(sort => sort.desc ? `-${sort.id}` : `+${sort.id}`)

  return {
    sorting,
    updateSorting,
    toggleSort,
    clearAllSorts,
    getSortDirection,
    getSorting,
  }
}

const parseSortParams = (params: string[]): SortingState => {
  if (params.length === 0) {return []}

  const lastParam = params[params.length - 1]
  const isDesc = lastParam.startsWith('-')
  const isAsc = lastParam.startsWith('+')

  if (!isDesc && !isAsc) {
    return []
  }

  const id = lastParam.slice(1)
  if (id.length === 0) {
    return []
  }

  return [{
    id,
    desc: isDesc,
  }]
}
